import { rem, radius, spacing, token } from '@thiememeulenhoff/lit-sdk';
import { NO_SCORE_TYPES, ScoreLevel } from '@thiememeulenhoff/types';
import { CSSResult, css, unsafeCSS } from 'lit';
import {
  SCORE_LEVEL_COLOR_HOVER_MAP,
  SCORE_LEVEL_COLOR_MAP,
  SCORE_LEVEL_TEXT_COLOR_MAP,
  SCORE_LEVEL_NEUTRAL_TEXT_COLOR_MAP,
  SCORE_LEVEL_BORDER_COLOR_MAP,
} from './score-label.types';

const activeBackground = token('score-label-active-background-default');

const forScoreLevel = (
  level: ScoreLevel,
  levelColor: CSSResult,
  textColor: CSSResult,
  hoverColor: CSSResult,
  neutralTextColor: CSSResult,
  borderColor: CSSResult,
) => css`
  :host([level='${unsafeCSS(level)}']) {
    background-color: ${levelColor};
    border: ${rem(1)} solid ${borderColor};
    color: ${textColor};
  }

  :host([active][level='${unsafeCSS(level)}']) {
    background-color: ${activeBackground};
    color: ${levelColor};
  }

  :host([active][level='${unsafeCSS(level)}']:hover) {
    background-color: ${activeBackground};
    color: ${levelColor};
  }

  :host([level='${unsafeCSS(level)}']:hover) {
    background-color: ${hoverColor};
  }

  :host([neutral][level='${unsafeCSS(level)}']) {
    color: ${neutralTextColor};
  }
`;

const scoreLevelsCss = Object.keys(SCORE_LEVEL_COLOR_MAP).map((label) =>
  forScoreLevel(
    label as ScoreLevel,
    SCORE_LEVEL_COLOR_MAP[label as ScoreLevel],
    SCORE_LEVEL_TEXT_COLOR_MAP[label as ScoreLevel],
    SCORE_LEVEL_COLOR_HOVER_MAP[label as ScoreLevel],
    SCORE_LEVEL_NEUTRAL_TEXT_COLOR_MAP[label as ScoreLevel],
    SCORE_LEVEL_BORDER_COLOR_MAP[label as ScoreLevel],
  ),
);

const noScoreTypesCss = NO_SCORE_TYPES.map(
  (scoreType) => css`
    :host([score-type='${unsafeCSS(scoreType)}'][level]) {
      background-color: ${token('score-label-success-background-default')};
      color: ${token('score-label-success-foreground-default')};
    }
    :host([active][score-type='${unsafeCSS(scoreType)}'][level]) {
      background-color: ${activeBackground};
      color: ${token('score-label-success-background-default')};
    }
    :host([score-type='${unsafeCSS(scoreType)}'][level]:hover) {
      background-color: ${token('score-label-success-background-hover')};
    }
  `,
);

export const scoreLabelStyles = [
  ...scoreLevelsCss,
  css`
    :host {
      align-items: center;
      block-size: ${rem(32)};
      border-radius: ${radius('small')};
      box-sizing: border-box;
      display: inline-flex;
      justify-content: center;
      padding: ${spacing(100, 200)};
    }


    :host([neutral]) {
      background-color: ${token('score-label-neutral-background-default')};
      border: ${rem(1)} solid ${token('score-label-neutral-border-default')};
      color: ${token('score-label-neutral-foreground-default')};
    }

    :host([neutral][level]:not([pill])) {
      background-color: ${token('score-label-neutral-filled-background-default')};
      color: ${token('score-label-neutral-filled-foreground-default')};
    }

    :host([pill]) {
      border-radius: ${radius('extra-large')};
    }

    :host([pill][neutral]),
    :host([pill][neutral][level]) {
      border: unset;
      background-color: ${token('score-label-neutral-background-default')};
      outline: ${rem(1)} solid ${token('score-label-neutral-pill-outline-default')};
      outline-offset: ${rem(-1)};
    }

    :host([pill][neutral]:not([level])) {
      color: ${token('score-label-neutral-pill-foreground-default')};
    }

    :host([pill][neutral]:hover),
    :host([pill][neutral][level]:hover) {
      background-color: ${token('score-label-neutral-pill-background-hover')};
    }

    :host([pill][neutral]:not([level]):hover) {
      color: ${token('score-label-neutral-pill-foreground-hover')};
    }

    :where(.score-icon) {
      min-inline-size: ${rem(16)};
    }
  }
  `,
  ...noScoreTypesCss,
];
